import { useModel as _useModel, useCssVars as _useCssVars, mergeModels as _mergeModels, defineComponent as _defineComponent } from 'vue'
import { unref as _unref, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "w-100 pa-2" }

import VueDatePicker from "@vuepic/vue-datepicker";
import "@vuepic/vue-datepicker/dist/main.css";
import moment from "moment-timezone";
import { computed, onMounted, ref, watch } from "vue";

interface IProps {
    allowedDates?: string[] | Date[] | ((date: unknown) => boolean);
    locale?: string;
    landscape?: boolean;
    color?: string;
    disabled?: boolean;
    height?: string | number; // #
    hideHeader?: boolean;
    max?: Date | string;
    min?: Date | string;
    multiple?: number | boolean | (string & {}) | "range";
    rounded?: string | number | boolean;
    title?: string;
    width?: string | number;
    border?: boolean;
    borderColor?: string;
    elevation?: string | number;
    minHeight?: string | number;
    maxHeight?: string | number;

    range?: boolean; // Equivalent of multiple = "range"
    fullWidth?: boolean;
    returnDateStringFormat?: string;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'MonthPicker',
  props: /*@__PURE__*/_mergeModels({
    allowedDates: {},
    locale: { default: "fr-FR" },
    landscape: { type: Boolean, default: false },
    color: { default: "primary" },
    disabled: { type: Boolean, default: false },
    height: { default: "auto" },
    hideHeader: { type: Boolean, default: false },
    max: {},
    min: {},
    multiple: { type: [Number, Boolean, String, Object], default: false },
    rounded: { type: [String, Number, Boolean], default: false },
    title: { default: "" },
    width: { default: "auto" },
    border: { type: Boolean, default: true },
    borderColor: { default: "rgba(0, 0, 0, 0.12)" },
    elevation: { default: 0 },
    minHeight: { default: "auto" },
    maxHeight: { default: "auto" },
    range: { type: Boolean, default: false },
    fullWidth: { type: Boolean, default: false },
    returnDateStringFormat: {}
  }, {
    "modelValue": {},
    "modelModifiers": {},
  }),
  emits: ["update:modelValue"],
  setup(__props: any) {

_useCssVars(_ctx => ({
  "506f2cbc": (activeCellColor.value),
  "4742be57": (borderRadius.value),
  "7f1719e8": (borderValue.value)
}))

/**
 * Month picker. Mimics to some extend the old <v-date-picker type='month' in vuetify 2.>,
 * while adding some props from vuetify 3 for a seemless integression.
 * Base component is <VueDatePicker> from https://vue3datepicker.com/props/modes/#month-picker
 */
const model = _useModel<string | string[] | Date | Date[]>(__props, "modelValue");

const toMonthPickerFormat = (date: string | Date | null) => {
    if (date == null) {
        return null;
    }
    const d = moment(date).toDate();
    return {
        month: d.getMonth(),
        year: d.getFullYear(),
    };
};

const internalValue = ref<{ month: number; year: number } | { month: number; year: number }[]>(
    Array.isArray(model.value)
        ? (model.value.map((m) => toMonthPickerFormat(m)) as { month: number; year: number }[])
        : toMonthPickerFormat(model.value),
);

const props = __props;

const disabledDates = (date: string | Date) => {
    // debugger;
    if (!props.allowedDates) {
        return false;
    }
    if (typeof props.allowedDates === "function") {
        const allowed = props.allowedDates(date);
        console.log("disabled", allowed, date);
        return !allowed;
    }
    return !props.allowedDates.some(
        (d) => moment(d).format("YYYY-MM") === moment(date).format("YYYY-MM"),
    );
};

const dateToType = (value, valueType) => {
    // debugger;
    if (valueType === "string") {
        const format = props.returnDateStringFormat || "YYYY-MM";
        return moment(value).format(format);
    } else if (valueType === "object" && value instanceof Date) {
        return moment(value).toDate();
    } else {
        if (props.returnDateStringFormat) {
            return moment(value).format(props.returnDateStringFormat);
        }
        return moment(value).toDate();
    }
};

// watch(
//     model,
//     (newDate, oldValue) => {
//         console.log("New Value", newDate, oldValue);
//     },
//     { deep: true },
// );

watch(
    internalValue,
    (newDate, oldValue) => {
        // debugger;
        // console.log("internalValue", newDate, oldValue);
        if (Array.isArray(newDate)) {
            const valueType = typeof model.value[0];
            model.value = [...new Set(newDate.map((date) => dateToType(date, valueType)))] as
                | string[]
                | Date[];
        } else {
            const valueType = typeof model.value;
            model.value = dateToType(newDate, valueType);
        }
    },
    { deep: true },
);

const style = computed(() => {
    return {
        height: props.height,
        width: props.width,
        minHeight: props.minHeight,
        maxHeight: props.maxHeight,
    };
});

const elevationClass = computed(() => {
    if (props.elevation) {
        return `elevation-${props.elevation}`;
    }
    return "";
});

const isRangeSelect = computed(() => {
    if (props.range || props.multiple === "range") {
        return true;
    }
    return false;
});

const borderRadiusMap = {
    0: 0,
    sm: "2px",
    md: "4px",
    lg: "8px",
    xl: "16px",
    xxl: "32px",
    true: "50%",
    false: 0,
};

const roundedClass = computed(() => {
    if (props.rounded === true) {
        return "rounded";
    } else if (props.rounded === false) {
        return "rounded-0";
    }
    return `rounded-${props.rounded}`;
});

const borderRadius = computed(() => {
    return borderRadiusMap?.[`${props.rounded}`] ?? "none";
});

const themeColor = computed(() => {
    if (props.color) {
        return `var(--v-theme-${props.color})`;
    }
    return "";
});

const activeCellColor = computed(() => {
    if (props.color) {
        return `rgb(var(--v-theme-${props.color}))`;
    }
    return "";
});

const borderColorValue = computed(() => {
    const rgbaRegex = /rgba\((\d+),\s*(\d+),\s*(\d+),\s*([\d.]+)\)/;
    const hexRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;
    const rgbRegex = /^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/;

    // debugger;
    if (
        rgbaRegex.test(props.borderColor) ||
        rgbRegex.test(props.borderColor) ||
        hexRegex.test(props.borderColor)
    ) {
        return props.borderColor;
    }
    return `rgb(var(--v-theme-${props.borderColor}))`;
});

const borderValue = computed(() => {
    // debugger;
    if (props.border) {
        console.log(`1px solid ${borderColorValue.value}`, borderColorValue.value);
        // 1px solid rgba(var(${themeColor}), var(--v-border-opacity))
        return `1px solid ${borderColorValue.value}`;
    } else {
        return "none";
    }
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_unref(VueDatePicker), {
      modelValue: internalValue.value,
      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((internalValue).value = $event)),
      inline: "",
      "auto-apply": "",
      "month-picker": "",
      "no-today": "",
      range: isRangeSelect.value,
      class: _normalizeClass({
                'w-100 d-flex flex-column': true,
                [elevationClass.value]: !!elevationClass.value,
                roundedClass: true,
            }),
      locale: _ctx.locale,
      "disabled-dates": disabledDates,
      disabled: _ctx.disabled,
      "max-date": _ctx.max,
      "min-date": _ctx.min,
      "prevent-min-max-navigation": true,
      "ignore-time-validation": "",
      ui: { calendarCell: roundedClass.value },
      style: _normalizeStyle(style.value),
      vertical: !_ctx.landscape
    }, null, 8, ["modelValue", "range", "class", "locale", "disabled", "max-date", "min-date", "ui", "style", "vertical"])
  ]))
}
}

})